import React from "react";
import { BookOpen, Leaf, BarChart2,Sprout, Users, HandHeart } from "lucide-react";
import { motion } from "framer-motion";
import HeadingBlock from "../Components/HeadingBlock";
import a1 from "../assets/bg6.jpg"; // make sure this image exists
import a2 from "../assets/w.jpg"; // make sure this image exists
import dgif from "../assets/dgif.gif";
import IconCard from "../Components/Iconcard";
import { Quote, Droplet, ShieldCheck, IndianRupee } from "lucide-react";
import CountUp from "react-countup";
import { Helmet } from "react-helmet";

const Sustainability = () => {
const services1 = [
  {
    icon: <HandHeart className="w-10 h-10 text-orange-900" />, 
    title: "Sustainable Livelihoods for Women SHGs",
    quote: "When women earn, communities rise.",
    description:
      "Drone Didi gives self-help groups the ability to generate a reliable income. Drones help women cultivate not only crops but also self-reliance, safety, and self-respect.",
  },
  {
    icon: <Leaf className="w-10 h-10 text-orange-900" />, 
    title: "Better Yields, Lower Costs",
    quote: "Smarter farming, stronger harvests.",
    description:
      "Precise spraying helps farmers save water, seeds, and fertilizers while ensuring healthy crops. This means higher yields at lower costs, creating a sustainable cycle of prosperity.",
  },
  {
    icon: <Sprout className="w-10 h-10 text-orange-900" />, 
    title: "Eco-Friendly Farming",
    quote: "Good for the soil, good for the soul.",
    description:
      "Drone technology reduces the excessive use of pesticides and fertilizers, reducing exposure of farmers, soil, and the environment to harmful chemicals. This shift supports cleaner agricultural practices and healthier food for all.",
  },
  {
    icon: <Users className="w-10 h-10 text-orange-900" />, 
    title: "Community Growth & Collaboration",
    quote: "One drone, many benefits.",
    description:
      "When SHGs rent drones to other farmers, they not only earn money – they also share innovation. This builds stronger farming communities, encourages collaboration, and spreads the benefits of technology far and wide.",
  },
];
const caseStudies = [
  {
    title: "From Farmer to Entrepreneur",
    name: "Rina Devi, Bihar",
    quote: "Earlier, I struggled to spray pesticides across my 4-acre farm. Now, with my drone, I finish in hours and rent services to 20 nearby farmers. I earn ₹1 lakh extra each year—and I feel proud to be called a Drone Didi.",
    impact: [
      { icon: <Sprout className="w-6 h-6 text-green-600" />, value: "250+", label: "Acres Sprayed" },
      { icon: <IndianRupee className="w-6 h-6 text-green-600" />, value: "₹1.1L", label: "Income Earned" },
      { icon: <ShieldCheck className="w-6 h-6 text-green-600" />, value: "30%", label: "Less Pesticide Use" },
    ],
  },
  {
    title: "Healthier Farming, Healthier Life",
    name: "Anita, Uttar Pradesh",
    quote: "Before, I would spend hours spraying manually, exposed to chemicals. Now, my drone keeps me safe while still ensuring my crops thrive. My health and my farm both are better today.",
    impact: [
      { icon: <Users className="w-6 h-6 text-green-600" />, value: "40+", label: "Farmers Protected" },
      { icon: <Droplet className="w-6 h-6 text-green-600" />, value: "20%", label: "Water Saved" },
      { icon: <Sprout className="w-6 h-6 text-green-600" />, value: "15%", label: "Yield Improved" },
    ],
  },
];



  return (
    <section>
       <Helmet>
        <title>Sustainable Development At Drone Didi</title>

        <meta
          name="description"
          content="With Drone Didi, sustainability is not just about crops, but about creating strong communities, empowered women and a future that thrives."
        />

        <link
          rel="canonical"
          href="https://dronedidi.co.in/sustainability"
        />
      </Helmet>
      <div className="md:max-w-[95%] mx-auto flex flex-col">
        <div
          className="relative bg-fixed bg-center bg-cover py-16 px-6 md:px-16"
          style={{ backgroundImage: `url(${a1})` }} // your background image
        >
          {/* Overlay for better readability */}
          <div className="absolute inset-0 bg-black/50"></div>

          {/* Content */}
          <section className="relative max-w-7xl mx-auto px-4 lg:px-8 py-16">
            {/* Main content */}
            <motion.div 
             initial={{ opacity: 0, y: 50 }}
  whileInView={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.9, ease: "easeOut" }}
  viewport={{ once: true }}
            className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left: Circle Image + Drone GIF */}
              <div className="relative flex justify-center lg:justify-start">
                {/* Circle Image */}
                <div className="relative md:h-[500px] md:w-[500px] p-2 bg-white rounded-full overflow-hidden shadow-2xl">
                  <img
                    src={a2}
                    alt="Drone Training"
                    className="w-full h-full object-cover rounded-full"
                  />
                </div>

                {/* Drone GIF flying smoothly */}
                <motion.img
                  src={dgif}
                  alt="Drone flying"
                  className="absolute w-40 md:w-56 -top-10 -left-10 z-10"
                  initial={{ x: 0, y: 0 }}
                  animate={{ x: [0, 20, 0], y: [0, 15, 0] }} // smooth horizontal/vertical movement
                  transition={{
                    repeat: Infinity,
                    duration: 3,
                    ease: "easeInOut",
                  }}
                />
              </div>

              {/* Right: Text Content */}
              <div className="space-y-8 text-white">
                {/* Curriculum */}
                <div>
                  {/* Heading */}
                  <HeadingBlock
                    subtitle="Drones in the sky, progress on the ground."
                    title="Wings of Change, Roots of Hope"
                    align="left"
                    highlight="Women"
                    subtitleColor="text-white"
                    titleColor="text-white"
                  />
                  <p className="text-lg">
                    Sustainability here is a commitment we practice, not just
                    something we preach. We are creating stronger futures and
                    healthier communities in addition to healthier harvests by
                    fusing cutting-edge drone technology with women-led
                    agricultural methods. Every flight of a Drone Didi brings us
                    closer to an India where innovation and tradition walk hand
                    in hand for a sustainable tomorrow.
                  </p>
                </div>
              </div>
            </motion.div>
            {/* Empowerment Statement */}
            <div>
              <h3 className="text-2xl text-center font-bold text-white bg-[#4ade8099] p-3 my-4">
                How Drone Didi Builds Sustainability
              </h3>
 <div className="grid md:grid-cols-2 my-10 gap-8">
        {services1.map((service, index) => (
          <IconCard
            key={index} 
            icon={service.icon}
              // iconPosition="top-right"
                iconPosition="top-center"
  cardBg="bg-[#bde2ca]"      // dark red
  iconBg="bg-[#ff953f]"          // light blue
            title={service.title}
            description={service.description}
            borderGradient="from-orange-200 to-orange-600"
            quote={service.quote}
          />
        ))}
      </div>
          
              <p className="text-2xl text-center p-8 bg-[#fb923cb5] mt-5 text-white">
                With Drone Didi, sustainability is not just about crops, but about creating strong communities, empowered women, and a future that thrives.
              </p>
            </div>
          </section>
        </div>


    <section className="py-10 bg-gradient-to-b from-green-50 to-white">
  <div className="max-w-7xl mx-auto px-6">
    <HeadingBlock
      subtitle="Stories of Change"
      title="How Drone Didis are transforming"
      align="left"
      highlight="farming and communities"
      subtitleColor="text-black"
      titleColor="text-black"
    />

    {/* Cards */}
    <div className="space-y-12 mt-10">
      {caseStudies.map((c, i) => (
        <div
          key={i}
          className="bg-white rounded-3xl shadow-lg overflow-hidden flex flex-col md:flex-row hover:shadow-2xl transition duration-300"
        >
          {/* Left side decorative */}
          <div className="md:w-1/2 lg:w-1/3 bg-green-100 flex items-center justify-center p-6 relative">
            <Quote className="absolute text-orange-300 opacity-35 w-24 h-24 top-6 left-6 rotate-180" />
            <h3 className="text-4xl font-bold text-green-900 z-10 text-center">
              {c.title}
            </h3>
          </div>

          {/* Right side content */}
          <div className="md:w-2/3 p-8 flex flex-col">
            {/* Name */}
            <p className="text-md text-gray-500 mb-3">{c.name}</p>

            {/* Quote */}
            <p className="italic text-lg text-gray-700 leading-relaxed mb-8 relative">
              “{c.quote}”
            </p>

            {/* Impact Stats */}
            <div className="grid lg:grid-cols-3 gap-4">
              {c.impact.map((imp, idx) => (
                <div
                  key={idx}
                  className="bg-green-50 rounded-xl p-4 flex flex-col items-center text-center hover:bg-green-100 transition"
                >
                  {imp.icon}
                  <p className="text-2xl font-bold text-gray-900 mt-2">
                    <CountUp
                      end={parseInt(imp.value.replace(/[^0-9]/g, ""))} // numbers only
                      duration={2}
                      enableScrollSpy
                    />
                    {imp.value.replace(/[0-9]/g, "")} {/* suffix like % / + */}
                  </p>
                  <p className="text-md text-gray-600">{imp.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
</section>
     
      </div>
    </section>
  );
};

export default Sustainability;
